import { getDownloadURL, ref, uploadBytes, UploadResult } from 'firebase/storage'
import { Customer, NewCustomerTDO } from '../types/customer'
import { storage } from '../utils/firebase'
import { graphQLClient } from './authService'
import customerMutations from './graphql/customers/mutations'
import customerQueries from './graphql/customers/queries'

type uploadFallbackImageProps = {
  file: File
  customerId?: string
}

class CustomerService {
  getAll = async (params: { [x: string]: string }, variables: any = {}): Promise<Customer[]> => {
    const where = {
      OR: [{ name_contains: params.search }],
    }

    const response = await graphQLClient.request(customerQueries.getAll, {
      where,
      ...variables,
    })
    return response.customers
  }

  uploadImage = async ({
    file,
    customerId, // handles both existing and new customer
  }: uploadFallbackImageProps): Promise<UploadResult> => {
    const timestamp = new Date().getTime()
    const fileRoute = customerId
      ? `/customers/${customerId}/${timestamp}-${file.name}`
      : `/customers/${timestamp}-${file.name}`
    const result = await uploadBytes(ref(storage, fileRoute), file)

    return result
  }

  createCustomer = async ({
    fallbackImage,
    avatar,
    places,
    ...rest
  }: Partial<Omit<NewCustomerTDO, 'avatar' | 'fallbackImage'>> & {
    avatar: UploadResult
    fallbackImage: UploadResult
  }): Promise<Customer> => {
    const avatarUrl = await getDownloadURL(avatar.ref)
    const fallbackUrl = await getDownloadURL(fallbackImage.ref)

    const response = await graphQLClient.request(customerMutations.createCustomer, {
      data: {
        ...rest,
        googlePlaceIds: places ? places.map(({ googlePlaceId }) => googlePlaceId) : [],
        avatar: {
          create: {
            url: avatarUrl,
            reference: avatar.ref.fullPath,
          },
        },
        fallbackImage: {
          create: {
            url: fallbackUrl,
            reference: fallbackImage.ref.fullPath,
          },
        },
      },
    })

    return response.createCustomer
  }

  updateCustomer = async ({
    fallbackImage,
    avatar,
    id,
    ...rest
  }: Partial<Omit<NewCustomerTDO, 'avatar' | 'fallbackImage' | 'places'>> & {
    avatar?: UploadResult
    fallbackImage?: UploadResult
    id: string
  }): Promise<Customer> => {
    const data: any = {
      ...rest,
    }

    if (avatar) {
      const avatarUrl = await getDownloadURL(avatar.ref)
      data.avatar = avatar
        ? {
            replace: {
              url: avatarUrl,
              reference: avatar.ref.fullPath,
            },
          }
        : {}
    }

    if (fallbackImage) {
      const fallbackUrl = await getDownloadURL(fallbackImage.ref)
      data.fallbackImage = fallbackImage
        ? {
            replace: {
              url: fallbackUrl,
              reference: fallbackImage.ref.fullPath,
            },
          }
        : {}
    }

    const response = await graphQLClient.request(customerMutations.updateCustomer, {
      where: {
        id: id,
      },
      data,
    })

    return response.updateCustomer
  }

  deleteCustomer = async ({ id }: Pick<Customer, 'id'>) => {
    const response = await graphQLClient.request(customerMutations.deleteCustomer, {
      where: {
        id_in: [id],
      },
    })

    return response.deleteCustomers
  }
}

const customerService = new CustomerService()

export default customerService
